import { useEffect, useRef, type Dispatch, type SetStateAction, type RefObject } from 'react';
import {
  handleRunnerBoosterCollisions,
  handleRunnerModifierZoneEnter,
  handleRunnerObstacleCollisions,
  handleRunnerSparkCollisions,
} from '../../../../../application/runner/runnerCollisionUseCases';
import { tickRunnerPathObstaclesUseCase } from '../../../../../application/runner/runnerPathObstacleUseCases';
import { tickRunnerPathDecorsUseCase } from '../../../../../application/runner/runnerPathDecorUseCases';
import { tickRunnerLevelUseCase } from '../../../../../application/runner/runnerUseCases';
import type { CourierId } from '../../../../../domain/ids/ids';
import { hasRunnerReachedFinish } from '../../../../../domain/runner/runnerLevelRules';
import { RUNNER_LANE_MAX, RUNNER_MAX_DELTA_SECONDS } from '../../../../../domain/runner/runnerConstants';
import type { RunnerLevel, RunnerPlayer, VerticalMoveDirection } from '../../../../../domain/runner/runnerTypes';
import {
  playRunnerBooster,
  playRunnerHit,
  playRunnerSpark,
} from '../../../../assets/preloadSounds';
import { PLAYER_TILT_DEGREES } from '../gameplayConstants';
import {
  computeWorldTranslateXPx,
  projectSvgPointInWorld,
} from '../layout/runnerLevelLayout';
import {
  applyEntityPose,
  applyPlayfieldCamera,
  applyPlayerPose,
  applyProgressFill,
  type PlayfieldMotionBindings,
} from '../playfieldMotionBindings';
import {
  levelNeedsReactPublish,
  playerNeedsReactPublish,
} from '../runnerDiscretePublish';

export type RunnerTickCollisionHandlers = {
  onSparksCollected: (count: number) => void;
  onSpeedBoostersCollected: (count: number) => void;
  onObstacleHit?: (livesAfterHit: number) => void;
  onBoosterCollected?: () => void;
};

/**
 * RAF owns continuous sim + DOM camera/player; React setState only on discrete deltas.
 */
export function useRunnerLevelTick(
  level: RunnerLevel | null,
  player: RunnerPlayer | null,
  setPlayer: Dispatch<SetStateAction<RunnerPlayer | null>>,
  setLevel: Dispatch<SetStateAction<RunnerLevel | null>>,
  courierId: CourierId,
  getVerticalDirection: () => VerticalMoveDirection,
  onFrame?: (deltaSeconds: number) => void,
  isPaused = false,
  getMotionScale: () => number = () => 1,
  motionBindings?: RefObject<PlayfieldMotionBindings | null>,
  getDisplayTilt: () => number = () => 0,
  collisionsEnabled = true,
  collisionHandlers?: RunnerTickCollisionHandlers,
  simLevelRef?: RefObject<RunnerLevel | null>,
  simPlayerRef?: RefObject<RunnerPlayer | null>,
): void {
  const levelRef = useRef(level);
  levelRef.current = level;
  const playerRef = useRef(player);
  playerRef.current = player;
  const onFrameRef = useRef(onFrame);
  onFrameRef.current = onFrame;
  const isPausedRef = useRef(isPaused);
  isPausedRef.current = isPaused;
  const getMotionScaleRef = useRef(getMotionScale);
  getMotionScaleRef.current = getMotionScale;
  const getVerticalDirectionRef = useRef(getVerticalDirection);
  getVerticalDirectionRef.current = getVerticalDirection;
  const courierIdRef = useRef(courierId);
  courierIdRef.current = courierId;
  const getDisplayTiltRef = useRef(getDisplayTilt);
  getDisplayTiltRef.current = getDisplayTilt;
  const collisionsEnabledRef = useRef(collisionsEnabled);
  collisionsEnabledRef.current = collisionsEnabled;
  const collisionHandlersRef = useRef(collisionHandlers);
  collisionHandlersRef.current = collisionHandlers;
  const motionBindingsRef = useRef(motionBindings);
  motionBindingsRef.current = motionBindings;
  const simLevelRefExternal = simLevelRef;
  const simPlayerRefExternal = simPlayerRef;

  const obstacleActiveRef = useRef<Set<string>>(new Set());
  const sparkActiveRef = useRef<Set<string>>(new Set());
  const boosterActiveRef = useRef<Set<string>>(new Set());

  const sessionKey = level == null ? null : `${level.giantId}:${level.startPointSvgX}`;

  useEffect(() => {
    obstacleActiveRef.current = new Set();
    sparkActiveRef.current = new Set();
    boosterActiveRef.current = new Set();
  }, [sessionKey]);

  useEffect(() => {
    if (sessionKey == null) {
      return;
    }

    let frameId = 0;
    let lastTimestamp = performance.now();

    const publishDom = (nextLevel: RunnerLevel, nextPlayer: RunnerPlayer): void => {
      const bindings = motionBindingsRef.current?.current;
      if (bindings == null || bindings.displayScale <= 0) {
        return;
      }
      const lead = Math.max(
        0,
        bindings.courierScreenX / bindings.displayScale -
          nextPlayer.worldS -
          bindings.spawnSvgX,
      );
      const translateX = computeWorldTranslateXPx(
        nextPlayer.worldS,
        lead,
        bindings.courierScreenX,
        bindings.displayScale,
        bindings.spawnSvgX,
      );
      applyPlayfieldCamera(bindings, translateX);

      const svgX = bindings.startPointSvgX + nextPlayer.worldS;
      const svgY = (RUNNER_LANE_MAX - nextPlayer.lane) * bindings.viewBoxHeight;
      const pose = projectSvgPointInWorld(svgX, svgY, {
        canvasLeftPx: 0,
        canvasTopPx: bindings.canvasTopPx,
        canvasWidthPx: 0,
        canvasHeightPx: 0,
        contentOffsetPx: 0,
        displayScale: bindings.displayScale,
      });
      const tiltDeg = -getDisplayTiltRef.current() * PLAYER_TILT_DEGREES;
      applyPlayerPose(bindings, pose.leftPx, pose.topPx, tiltDeg);

      const progress =
        bindings.routeEndS > 0
          ? Math.min(1, nextPlayer.worldS / bindings.routeEndS)
          : 0;
      applyProgressFill(bindings, progress);

      for (const obstacle of nextLevel.obstacles) {
        if (obstacle.motion == null || obstacle.consumed || (obstacle.opacity ?? 1) <= 0) {
          continue;
        }
        const centerS = obstacle.visualCenterS ?? obstacle.hitbox.centerS;
        const centerLane = obstacle.visualCenterLane ?? obstacle.hitbox.centerLane;
        const oSvgX = bindings.startPointSvgX + centerS;
        const oSvgY = (RUNNER_LANE_MAX - centerLane) * bindings.viewBoxHeight;
        const oPose = projectSvgPointInWorld(oSvgX, oSvgY, {
          canvasLeftPx: 0,
          canvasTopPx: bindings.canvasTopPx,
          canvasWidthPx: 0,
          canvasHeightPx: 0,
          contentOffsetPx: 0,
          displayScale: bindings.displayScale,
        });
        applyEntityPose(bindings, obstacle.id, oPose.leftPx, oPose.topPx);
      }
      for (const decor of nextLevel.decors) {
        if (decor.motion == null || (decor.opacity ?? 1) <= 0) {
          continue;
        }
        const dSvgX = bindings.startPointSvgX + decor.centerS;
        const dSvgY = (RUNNER_LANE_MAX - decor.centerLane) * bindings.viewBoxHeight;
        const dPose = projectSvgPointInWorld(dSvgX, dSvgY, {
          canvasLeftPx: 0,
          canvasTopPx: bindings.canvasTopPx,
          canvasWidthPx: 0,
          canvasHeightPx: 0,
          contentOffsetPx: 0,
          displayScale: bindings.displayScale,
        });
        applyEntityPose(bindings, decor.id, dPose.leftPx, dPose.topPx);
      }
    };

    const runCollisions = (
      currentLevel: RunnerLevel,
      currentPlayer: RunnerPlayer,
    ): { level: RunnerLevel; player: RunnerPlayer } => {
      if (!collisionsEnabledRef.current) {
        return { level: currentLevel, player: currentPlayer };
      }
      const handlers = collisionHandlersRef.current;
      let nextLevel = currentLevel;
      let nextPlayer = currentPlayer;

      const obstacleBefore = obstacleActiveRef.current.size;
      const obstacleResult = handleRunnerObstacleCollisions(
        nextLevel,
        nextPlayer,
        obstacleActiveRef.current,
      );
      nextLevel = obstacleResult.level;
      nextPlayer = obstacleResult.player;
      obstacleActiveRef.current = obstacleResult.nextActiveCollisionIds;
      if (obstacleResult.nextActiveCollisionIds.size > obstacleBefore) {
        playRunnerHit();
        handlers?.onObstacleHit?.(nextPlayer.lives);
      }

      const sparkResult = handleRunnerSparkCollisions(
        nextLevel,
        nextPlayer,
        sparkActiveRef.current,
      );
      nextLevel = sparkResult.level;
      sparkActiveRef.current = sparkResult.nextActiveCollisionIds;
      if (sparkResult.sparksCollected > 0) {
        playRunnerSpark();
        handlers?.onSparksCollected(sparkResult.sparksCollected);
      }

      const boosterBefore = boosterActiveRef.current.size;
      const boosterResult = handleRunnerBoosterCollisions(
        nextLevel,
        nextPlayer,
        boosterActiveRef.current,
      );
      nextLevel = boosterResult.level;
      nextPlayer = boosterResult.player;
      boosterActiveRef.current = boosterResult.nextActiveCollisionIds;
      if (boosterResult.nextActiveCollisionIds.size > boosterBefore) {
        playRunnerBooster();
        handlers?.onBoosterCollected?.();
      }
      if (boosterResult.speedBoostersCollected > 0) {
        handlers?.onSpeedBoostersCollected(boosterResult.speedBoostersCollected);
      }

      const modifierResult = handleRunnerModifierZoneEnter(nextLevel, nextPlayer);
      nextLevel = modifierResult.level;

      return { level: nextLevel, player: nextPlayer };
    };

    const tick = (timestamp: number): void => {
      if (isPausedRef.current) {
        lastTimestamp = timestamp;
        frameId = requestAnimationFrame(tick);
        return;
      }

      const currentLevel =
        simLevelRefExternal?.current ?? levelRef.current;
      const current = simPlayerRefExternal?.current ?? playerRef.current;
      if (currentLevel == null) {
        frameId = requestAnimationFrame(tick);
        return;
      }

      const motionScale = getMotionScaleRef.current();
      // Do not stop the loop on death — second chance may restore lives and resume.
      if (current != null && current.lives <= 0) {
        lastTimestamp = timestamp;
        frameId = requestAnimationFrame(tick);
        return;
      }
      if (
        current != null &&
        motionScale <= 0 &&
        hasRunnerReachedFinish(currentLevel, current)
      ) {
        return;
      }

      const deltaSeconds = Math.min(
        (timestamp - lastTimestamp) / 1000,
        RUNNER_MAX_DELTA_SECONDS,
      );
      lastTimestamp = timestamp;

      const verticalDirection = getVerticalDirectionRef.current();
      onFrameRef.current?.(deltaSeconds);

      let nextPlayer = current;
      if (current != null && current.lives > 0) {
        const scale = getMotionScaleRef.current();
        if (!(scale <= 0 && hasRunnerReachedFinish(currentLevel, current))) {
          nextPlayer = tickRunnerLevelUseCase(
            currentLevel,
            current,
            courierIdRef.current,
            verticalDirection,
            deltaSeconds,
            scale,
          );
        }
      }

      const scale = getMotionScaleRef.current();
      let nextLevel = tickRunnerPathObstaclesUseCase(
        currentLevel,
        deltaSeconds,
        scale,
      );
      nextLevel = tickRunnerPathDecorsUseCase(nextLevel, deltaSeconds, scale);

      if (nextPlayer != null) {
        const collided = runCollisions(nextLevel, nextPlayer);
        nextLevel = collided.level;
        nextPlayer = collided.player;
      }

      if (simLevelRefExternal != null) {
        simLevelRefExternal.current = nextLevel;
      }
      if (simPlayerRefExternal != null && nextPlayer != null) {
        simPlayerRefExternal.current = nextPlayer;
      }
      levelRef.current = nextLevel;
      if (nextPlayer != null) {
        playerRef.current = nextPlayer;
      }

      if (nextPlayer != null) {
        publishDom(nextLevel, nextPlayer);
      }

      if (nextPlayer != null && current != null && playerNeedsReactPublish(current, nextPlayer)) {
        setPlayer(nextPlayer);
      }
      if (levelNeedsReactPublish(currentLevel, nextLevel)) {
        setLevel(nextLevel);
      }

      frameId = requestAnimationFrame(tick);
    };

    frameId = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(frameId);
    };
  }, [
    sessionKey,
    setPlayer,
    setLevel,
    simLevelRefExternal,
    simPlayerRefExternal,
  ]);
}
